<template>
  <ModalForm
    v-model="isModalOpen"
    :data="store.personalDetails as unknown as Record<string, unknown> ?? {}"
    :title="$t('pages.onboarding.personal.edit.heading')"
    :on-submit="handleForm"
    disable-submit-on-error>
    <p class="subheader">
      {{ $t('pages.onboarding.personal.edit.subtitle.info') }}
    </p>
    <div class="half-wrapper">
      <FormKit
        type="TextPatternInput"
        name="firstName"
        :label="$t('form.label.first_name')"
        :pattern="shortNameRule.pattern"
        :validation="shortNameRule.validation"/>

      <FormKit
        type="TextPatternInput"
        name="lastName"
        :label="$t('form.label.last_name')"
        :pattern="shortNameRule.pattern"
        :validation="shortNameRule.validation"/>
    </div>
    <div class="half-wrapper">
      <FormKit
        type="CountryDropdown"
        name="nationalityIso3"
        :label="$t('form.label.nationality')"
        value-key="shortName"
        :options="ALL_COUNTRIES"
        validation="required"/>
      <FormKit
        type="DateInput"
        name="dateOfBirth"
        :label="$t('form.label.date_of_birth')"
        :validation="`required|date_before:${new Date(time.eighteenYearsAgo)}`"/>
    </div>
    <div class="half-wrapper">
      <FormKit
        type="SearchDropdown"
        name="fundsSource"
        :label="$t('form.label.funds_source')"
        label-key="label"
        value-key="value"
        :options="fundsSource"
        validation="required"/>
      <FormKit
        type="SearchDropdown"
        :label="$t('form.label.estimated_annual_turnover')"
        name="annualTurnover"
        :options="annualTurnovers"
        label-key="label"
        value-key="value"
        validation="required"/>
    </div>
    <FormKit
      type="TextPatternTextArea"
      :label="$t('form.label.occupation')"
      name="occupation"
      inner-class="h-135 w-full"
      wrapper-class="w-full max-w-full"
      :pattern="OccupationFormKitRule.pattern"
      :validation="OccupationFormKitRule.validation"/>
    <p class="subheader">
      {{ $t('pages.onboarding.personal.edit.subtitle.address') }}
    </p>
    <FormKit
      type="group"
      name="mailingAddress">
      <div class="grid grid-cols-2 gap-40">
        <AddressFields/>
      </div>
    </FormKit>
  </ModalForm>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';

import ModalForm from '@/components/modals/ModalForm.vue';
import { ALL_COUNTRIES } from '@/constants/countries';
import AddressFields from '@/modules/onboarding/components/AddressFields.vue';
import { NameFormKitRule } from '@/rules/formkit-rules/Name';
import { OccupationFormKitRule } from '@/rules/formkit-rules/Occupation';
import { requestHandler } from '@/services/requestHandler';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import { usePersonalOnboardingStore } from '@/stores/OnboardingPersonal';
import { getOptionsList } from '@/utils/onboarding';
import { time } from '@/utils/time';

const isModalOpen = defineModel<boolean>('isModalOpen', { required: true });

const store = usePersonalOnboardingStore();

const { params: { id } } = useRoute();

const fundsSource = getOptionsList('FundsSource', true);
const annualTurnovers = getOptionsList('AnnualTurnover');

const handleForm = async (data: Onboarding.Personal.PersonalDetails) => {
  await requestHandler(() => store.personalDetailsAdminReq.updateUser(id, data), {
    onSuccess: () => {
      isModalOpen.value = false;
    },
  });
};

const shortNameRule = NameFormKitRule(50);
</script>

<style lang="scss" scoped>
.subheader {
  @apply font-semibold text-22;
}
</style>
